import { useState } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "@/components/feature/PublicNavbar";
import PublicFooter from "@/components/feature/PublicFooter";
import { useBranding } from "@/contexts/BrandingContext";
import PageMeta from "@/components/feature/PageMeta";

type LegalTab = "privacidad" | "terminos";

export default function LegalPage() {
  const branding = useBranding();
  const [activeTab, setActiveTab] = useState<LegalTab>("privacidad");

  const tabs: { key: LegalTab; label: string; icon: string }[] = [
    { key: "privacidad", label: "Política de Privacidad", icon: "ri-shield-keyhole-line" },
    { key: "terminos", label: "Términos y Condiciones", icon: "ri-file-text-line" },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col">
      <PageMeta title={`Legal | ${branding.site_name}`} path="/legal" />
      <PublicNavbar />

      {/* HERO BANNER */}
      <section className="relative pt-[72px] pb-12 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[var(--color-bg-secondary)]">
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent-oil)]/5 via-transparent to-transparent"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-[var(--color-accent-oil)]/3 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-[var(--color-accent-oil)]/3 rounded-full blur-[100px]"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto pt-10 lg:pt-14">
            <div className="flex items-center gap-3 text-sm text-[var(--color-text-muted)] mb-6">
              <Link to="/" className="hover:text-[var(--color-accent-oil)] transition-colors">Inicio</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-[var(--color-text-secondary)]">Legal</span>
            </div>

            <img
              src={branding.logo_full}
              alt="KSM Services"
              className="h-8 sm:h-10 w-auto mb-6 drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
            />

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[var(--color-text-primary)] mb-3 tracking-tight">
              Información Legal
            </h1>
            <p className="text-base text-[var(--color-text-secondary)] max-w-xl">
              Conozca nuestros términos de uso y nuestra política de privacidad. La transparencia es parte de nuestro compromiso con la excelencia.
            </p>
          </div>
        </div>
      </section>

      {/* TABS + CONTENT */}
      <section className="flex-1 pb-20 lg:pb-28 -mt-4 relative z-10">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">

            {/* Tab Switcher */}
            <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-2xl p-1.5 mb-10 flex gap-1 shadow-lg shadow-black/5">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? "bg-[var(--color-accent-oil)] text-[#080f1a] shadow-md shadow-[#D4A03C]/15"
                      : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card)]/50"
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-2xl p-8 sm:p-10 lg:p-12 shadow-lg shadow-black/5">
              {activeTab === "privacidad" && <PrivacyPolicyContent />}
              {activeTab === "terminos" && <TermsContent />}
            </div>

            {/* Last Updated */}
            <p className="text-xs text-[var(--color-text-muted)] text-center mt-8">
              Última actualización: 13 de junio de 2026 — KSM Training, Villahermosa, Tabasco, México
            </p>

          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function PrivacyPolicyContent() {
  return (
    <div className="prose-custom">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/10 border border-[var(--color-accent-oil)]/20 flex items-center justify-center">
          <i className="ri-shield-keyhole-line text-[var(--color-accent-oil)] text-lg"></i>
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--color-text-primary)]">Política de Privacidad</h2>
          <p className="text-sm text-[var(--color-text-muted)]">KSM Training / KASUMAR</p>
        </div>
      </div>

      <div className="space-y-8 text-[var(--color-text-secondary)] text-sm leading-relaxed">
        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">1. Identidad del Responsable</h3>
          <p>
            KSM Training, operando bajo la razón social KASUMAR, con domicilio en Villahermosa, Tabasco, México, es el responsable del tratamiento de los datos personales que usted nos proporcione. Nuestro compromiso es proteger su privacidad y garantizar la seguridad de su información personal en cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y demás normatividad aplicable en los Estados Unidos Mexicanos.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">2. Datos Personales que Recabamos</h3>
          <p className="mb-3">Para proporcionar nuestros servicios de capacitación, consultoría y suministros, podemos recabar las siguientes categorías de datos personales:</p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Datos de identificación:</strong> Nombre completo, fecha de nacimiento, CURP, RFC, nacionalidad.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Datos de contacto:</strong> Correo electrónico, número telefónico, dirección, empresa y cargo.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Datos académicos y profesionales:</strong> Historial de capacitación, certificaciones previas, experiencia laboral en el sector Oil & Gas.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Datos de emergencia:</strong> Nombre y teléfono de contacto de emergencia para cursos presenciales.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Datos de facturación:</strong> RFC, razón social, domicilio fiscal, método de pago.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">3. Finalidades del Tratamiento</h3>
          <p className="mb-3">Sus datos personales serán utilizados para las siguientes finalidades:</p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Procesar su registro e inscripción a los cursos de capacitación presencial.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Emitir certificados oficiales con validez NOM e ISO 9001, incluyendo código QR de verificación.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Gestionar pagos, generar recibos y facturación electrónica.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Comunicar información sobre cursos, fechas, cambios en la programación y nuevos servicios.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Realizar evaluaciones de desempeño y satisfacción de los servicios prestados.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Cumplir con obligaciones legales aplicables en materia fiscal, laboral y de seguridad industrial.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">4. Transferencia de Datos</h3>
          <p>
            KSM Training se compromete a no vender, ceder ni transferir sus datos personales a terceros sin su consentimiento previo, salvo en los casos previstos por la ley. Podremos compartir información con autoridades competentes cuando sea requerido por mandato legal, o con instituciones certificadoras (como organismos acreditadores de la NOM e ISO) exclusivamente para fines de validación de certificados emitidos.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">5. Derechos ARCO</h3>
          <p className="mb-3">
            Usted tiene derecho a ejercer sus derechos de Acceso, Rectificación, Cancelación y Oposición (ARCO) respecto a sus datos personales. Para ejercerlos, deberá enviar una solicitud por escrito al correo electrónico <a href="mailto:privacidad@kasumar.com" className="text-[var(--color-accent-oil)] hover:underline font-medium">privacidad@kasumar.com</a>, incluyendo:
          </p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Nombre completo del titular.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Documento que acredite su identidad (INE, pasaporte o cédula profesional).</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Descripción clara y precisa de los datos respecto de los cuales busca ejercer algún derecho ARCO.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Correo electrónico o domicilio para recibir notificaciones.</span>
            </li>
          </ul>
          <p className="mt-3">El tiempo de respuesta a su solicitud no excederá de 20 días hábiles contados a partir de la recepción de la misma.</p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">6. Medidas de Seguridad</h3>
          <p>
            KSM Training implementa medidas de seguridad administrativas, técnicas y físicas para proteger sus datos personales contra daño, pérdida, alteración, destrucción o el uso, acceso o tratamiento no autorizado. Entre ellas se incluyen: encriptación de datos sensibles, control de acceso basado en roles, firewalls, monitoreo de actividad, y copias de seguridad periódicas.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">7. Uso de Cookies y Tecnologías de Rastreo</h3>
          <p>
            Nuestra plataforma web utiliza cookies esenciales para el funcionamiento de la sesión del usuario y cookies analíticas para mejorar la experiencia de navegación. No utilizamos cookies de publicidad comportamental ni compartimos datos de navegación con terceros con fines comerciales. Usted puede configurar su navegador para rechazar cookies; sin embargo, algunas funcionalidades del sitio podrían verse afectadas.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">8. Conservación de Datos</h3>
          <p>
            Sus datos personales serán conservados durante el tiempo necesario para cumplir con las finalidades descritas en este aviso de privacidad y para cumplir con las obligaciones legales aplicables. Los certificados emitidos y los registros académicos asociados se conservan de manera permanente para fines de verificación y trazabilidad, en cumplimiento con los requisitos de la norma ISO 9001:2015 y las Normas Oficiales Mexicanas aplicables al sector Oil & Gas.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">9. Modificaciones al Aviso de Privacidad</h3>
          <p>
            KSM Training se reserva el derecho de efectuar en cualquier momento modificaciones o actualizaciones al presente aviso de privacidad. Cualquier cambio será notificado a través de nuestra página web oficial y por correo electrónico a los usuarios registrados. Le recomendamos revisar periódicamente este aviso para mantenerse informado sobre cómo protegemos su información.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">10. Contacto</h3>
          <p>
            Para cualquier duda, queja o sugerencia relacionada con el tratamiento de sus datos personales, puede contactarnos a través de:
          </p>
          <ul className="space-y-2 pl-5 mt-3">
            <li className="flex items-start gap-2">
              <i className="ri-mail-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Correo:</strong> <a href="mailto:privacidad@kasumar.com" className="text-[var(--color-accent-oil)] hover:underline">privacidad@kasumar.com</a></span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-phone-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Teléfono:</strong> +52 (993) 123 4567</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-map-pin-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Domicilio:</strong> Villahermosa, Tabasco, México</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="prose-custom">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-[var(--color-accent-oil)]/10 border border-[var(--color-accent-oil)]/20 flex items-center justify-center">
          <i className="ri-file-text-line text-[var(--color-accent-oil)] text-lg"></i>
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--color-text-primary)]">Términos y Condiciones</h2>
          <p className="text-sm text-[var(--color-text-muted)]">KSM Training / KASUMAR</p>
        </div>
      </div>

      <div className="space-y-8 text-[var(--color-text-secondary)] text-sm leading-relaxed">
        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">1. Aceptación de los Términos</h3>
          <p>
            Al acceder y utilizar la plataforma de KSM Training (en adelante, "la Plataforma"), operada por KASUMAR, usted acepta quedar vinculado por estos Términos y Condiciones. Si no está de acuerdo con alguna de las disposiciones aquí establecidas, le solicitamos que se abstenga de utilizar nuestros servicios. Estos términos aplican a todos los usuarios de la Plataforma, incluyendo estudiantes, instructores, evaluadores, administradores y visitantes.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">2. Descripción de los Servicios</h3>
          <p className="mb-3">
            KSM Training proporciona los siguientes servicios a través de su Plataforma:
          </p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Capacitación presencial:</strong> Cursos especializados para el sector Oil & Gas, seguridad industrial y salud ocupacional.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Consultoría técnica:</strong> Asesoría especializada en procesos industriales, normatividad y mejora continua.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Suministros industriales:</strong> Equipos y materiales para operaciones en el sector energético.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Certificación oficial:</strong> Emisión de certificados con validez NOM e ISO 9001, incluyendo verificación QR y trazabilidad completa.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">3. Registro y Cuenta de Usuario</h3>
          <p className="mb-3">
            Para acceder a ciertos servicios de la Plataforma, es necesario crear una cuenta de usuario proporcionando información veraz, precisa y completa. Usted es responsable de:
          </p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Mantener la confidencialidad de sus credenciales de acceso (usuario y contraseña).</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Notificar inmediatamente a KSM Training sobre cualquier uso no autorizado de su cuenta.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Actualizar sus datos de contacto y perfil cuando existan cambios.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Asumir la responsabilidad de todas las actividades que ocurran bajo su cuenta.</span>
            </li>
          </ul>
          <p className="mt-3">
            KSM Training se reserva el derecho de suspender o cancelar cuentas que proporcionen información falsa, incurran en actividades fraudulentas o violen estos Términos y Condiciones.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">4. Inscripción y Pagos</h3>
          <p className="mb-3">
            La inscripción a los cursos de capacitación está sujeta a disponibilidad de cupo y al pago correspondiente. Las condiciones de pago son las siguientes:
          </p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Los precios de los cursos se expresan en dólares (USD) e incluyen el IVA aplicable, salvo indicación expresa en contrario.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>El pago puede realizarse mediante transferencia bancaria, depósito o los métodos indicados en la Plataforma.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>El comprobante de pago deberá enviarse al correo <a href="mailto:cursos@kasumar.com" className="text-[var(--color-accent-oil)] hover:underline font-medium">cursos@kasumar.com</a> para confirmar la inscripción.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>KSM Training emitirá el recibo oficial de pago una vez confirmada la transacción.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">5. Política de Cancelación y Reembolso</h3>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Cancelaciones realizadas con al menos 7 días hábiles de anticipación a la fecha de inicio del curso: reembolso del 100% del monto pagado.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Cancelaciones realizadas entre 6 y 3 días hábiles antes del inicio: reembolso del 50%.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Cancelaciones con menos de 3 días hábiles de anticipación o inasistencia sin previo aviso: no aplica reembolso.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>En casos de fuerza mayor (debidamente acreditados), KSM Training evaluará cada situación de manera individual.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">6. Asistencia y Certificación</h3>
          <p className="mb-3">
            Para obtener el certificado oficial de un curso, el participante debe cumplir con los siguientes requisitos:
          </p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Asistencia mínima del 80% de las horas totales del curso según la NOM aplicable.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Aprobación por parte del evaluador designado tras cumplir con los criterios de evaluación establecidos.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>Haber liquidado el costo total del curso.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span>No haber incurrido en faltas graves al reglamento interno del curso o a las normas de seguridad.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">7. Propiedad Intelectual</h3>
          <p>
            Todo el contenido publicado en la Plataforma, incluyendo pero no limitado a textos, imágenes, logotipos, marcas, gráficos, diseños, materiales de capacitación, manuales, presentaciones, videos, evaluaciones y certificados, es propiedad exclusiva de KSM Training / KASUMAR o de sus respectivos licenciantes, y está protegido por las leyes de propiedad intelectual de los Estados Unidos Mexicanos y tratados internacionales aplicables. Queda estrictamente prohibida la reproducción, distribución, modificación o uso no autorizado de dicho contenido sin el consentimiento previo y por escrito de KSM Training.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">8. Conducta del Usuario</h3>
          <p className="mb-3">
            Los usuarios se comprometen a utilizar la Plataforma de manera responsable y respetuosa. Queda prohibido:
          </p>
          <ul className="space-y-2 pl-5">
            <li className="flex items-start gap-2">
              <i className="ri-forbid-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span>Utilizar la Plataforma para fines ilícitos, fraudulentos o no autorizados.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-forbid-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span>Interferir o interrumpir el funcionamiento de la Plataforma, sus servidores o redes conectadas.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-forbid-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span>Suplantar la identidad de otra persona o entidad, o falsear su afiliación.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-forbid-line text-red-400 mt-0.5 flex-shrink-0"></i>
              <span>Intentar acceder sin autorización a sistemas, cuentas o datos de otros usuarios.</span>
            </li>
          </ul>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">9. Limitación de Responsabilidad</h3>
          <p>
            KSM Training no será responsable por daños directos, indirectos, incidentales, especiales o consecuentes que resulten del uso o la imposibilidad de uso de la Plataforma. No garantizamos que el servicio sea ininterrumpido, libre de errores o completamente seguro. La información proporcionada en los cursos de capacitación tiene fines educativos y no sustituye la asesoría profesional específica para cada situación operativa. El participante asume la responsabilidad total por la aplicación de los conocimientos adquiridos en sus actividades profesionales.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">10. Legislación Aplicable y Jurisdicción</h3>
          <p>
            Estos Términos y Condiciones se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia derivada de la interpretación o ejecución de los mismos será sometida a la jurisdicción de los tribunales competentes en la ciudad de Villahermosa, Tabasco, renunciando expresamente a cualquier otro fuero que pudiera corresponderles por razón de domicilio presente o futuro.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">11. Modificaciones a los Términos</h3>
          <p>
            KSM Training se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor a partir de su publicación en la Plataforma. El uso continuado de los servicios después de dichas modificaciones constituye la aceptación de los nuevos términos. Se recomienda a los usuarios revisar periódicamente esta sección para estar al tanto de cualquier cambio.
          </p>
        </section>

        <section>
          <h3 className="text-base font-bold text-[var(--color-text-primary)] mb-3">12. Contacto Legal</h3>
          <p>
            Para cualquier asunto relacionado con estos Términos y Condiciones, puede contactarnos a través de:
          </p>
          <ul className="space-y-2 pl-5 mt-3">
            <li className="flex items-start gap-2">
              <i className="ri-mail-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Correo:</strong> <a href="mailto:legal@kasumar.com" className="text-[var(--color-accent-oil)] hover:underline">legal@kasumar.com</a></span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-phone-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Teléfono:</strong> +52 (993) 123 4567</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-map-pin-line text-[var(--color-accent-oil)] mt-0.5 flex-shrink-0"></i>
              <span><strong className="text-[var(--color-text-primary)]">Domicilio:</strong> Villahermosa, Tabasco, México</span>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}